import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  getInvestorList,
  getRmRedirectionLink,
} from '../../redux-store/actions/userManagement';
import {
  InvestorDetailEntity,
  RmRedirectionLinkType,
} from '../../redux-store/types/api-types';
import { formatToIndianCurrency } from '../../utils/utilityFunctions';
import { ColumnType, DataTable } from '../DataTable';
import { CommonLayout, AmountUnit } from '../commonComponents';
import DashboardLayout from './dashboard-layout';
import { TransactionIcon } from '../customSVGs';

const getModeCode = (mode: string | null | undefined): number | null => {
  if (!mode) return null;

  const m = mode.toLowerCase();

  if (m.includes('single')) return 1;
  if (m.includes('joint') || m.includes('jh')) return 2;
  if (m.includes('anyone') || m.includes('aos')) return 3;

  return null;
};

const headers = (): ColumnType[] => {
  return [
    {
      header: 'Investor Name',
      sortingKey: 'I_InvName',
      valueGetter: (row: InvestorDetailEntity) => row.I_InvName || 'N/A',
    },
    {
      header: 'Investor Type (LEC)',
      sortingKey: 'InvType',
      valueGetter: (row: InvestorDetailEntity) => {
        if (!row?.InvType) return 'N/A';
        return row?.LEC
          ? `${row.InvType} ${row.LEC}`
          : row.InvType;
      },
    },
    {
      header: 'Mode of Holding',
      sortingKey: 'ModeofHolding',
      valueGetter: (row: InvestorDetailEntity) => row.ModeofHolding || 'N/A',
    },
    {
      header: 'RM Name',
      sortingKey: 'rmname',
      valueGetter: (row: InvestorDetailEntity) => row.rmname || 'N/A',
    },
    {
      header: 'Invested Value',
      sortingKey: 'PurVal',
      valueGetter: (row: InvestorDetailEntity, amountUnit?: number | null | undefined) =>
        row?.PurVal || row?.PurVal === 0 ? `${formatToIndianCurrency(row?.PurVal, amountUnit)}` : 'N/A',
    },
    {
      header: 'Market Value',
      sortingKey: 'MktVal',
      valueGetter: (row: InvestorDetailEntity, amountUnit?: number | null | undefined) =>
        row?.MktVal || row?.MktVal === 0 ? `${formatToIndianCurrency(row?.MktVal, amountUnit)}` : 'N/A',
    },
    {
      header: 'Overall Gain/Loss',
      sortingKey: 'UnRealGainnLoss',
      renderEditDeleteCell: (row: InvestorDetailEntity, index: number, groupIndex?: number, amountUnit?: number | null | undefined): JSX.Element => {
        return (
          <Box sx={{ minWidth: 80 }}>
            {row?.UnRealGainnLoss || row?.UnRealGainnLoss === 0 ? (
              <>
                <Typography
                  sx={{
                    color: row.UnRealGainnLoss < 0 ? '#F26868' : '#07BC01',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                  }}>
                  {row.UnRealGainnLoss < 0
                    ? `- ${formatToIndianCurrency(row.UnRealGainnLoss * -1, amountUnit)}`
                    : `+ ${formatToIndianCurrency(row.UnRealGainnLoss, amountUnit)}`}
                </Typography>
              </>
            ) : (
              'N/A'
            )}
          </Box>
        );
      },
    },
    {
      header: '\xa0',
      renderEditDeleteCell: (row: InvestorDetailEntity): JSX.Element => {
        const dispatch = useDispatch();
        const history = useHistory();
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        };
        const handleClose = () => {
          setAnchorEl(null);
        };
        const handleRedirection = async () => {
          try {
            const updatedResponse = (await dispatch(
              getRmRedirectionLink({
                clientId: row.OClientID,
              })
            )) as unknown as RmRedirectionLinkType;
            window.open(updatedResponse?.link);
          } catch (e) {
            console.error((e as Error).message);
          }
          handleClose();
        };
        const handleClickViewDetails = () => {
          history.push('/investor', {
            investorId: row.OClientID,
            investorName: row.I_InvName,
          });
        };

        return (
          <>
            <Button
              sx={{ fontSize: 14, fontWeight: 500, p: 1, color: '#2057A6' }}
              variant="outlined"
              id="actions-btn"
              aria-controls={open ? 'actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              endIcon={<ArrowDropDownIcon />}>
              Actions
            </Button>
            <Menu
              id="actions-menu"
              anchorEl={anchorEl}
              open={open}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'actions-btn',
              }}>
              <MenuItem onClick={handleClickViewDetails}>
                <>
                  <VisibilityOutlinedIcon sx={{ width: 20, mr: 2 }} />
                  View Details
                </>
              </MenuItem>
              <MenuItem
                //  onClick={handleRedirection}
                onClick={() => {
                  history.push('/investor-reports', {
                    investorId: row.OClientID,
                    investorName: row.I_InvName,
                  });
                }}>
                <>
                  <FileUploadOutlinedIcon sx={{ width: 20, mr: 2 }} />
                  Download
                </>
              </MenuItem>
              <MenuItem onClick={() => {
                history.push('/investor-transactions', {
                  investorId: row.OClientID,
                  investorName: row.I_InvName,
                  modeOfHolding: getModeCode(row.ModeofHolding)
                });
              }}>
                <>
                  <Box sx={{ mr: 2 }}>
                    <TransactionIcon />
                  </Box>
                  Transaction
                </>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];
};

export default function InvestorDashboard(): JSX.Element {
  const [isLoading, setLoading] = useState(false);
  const [investorList, setInvestorList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>(''); // used for API
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const valueConverter = useSelector((state: any) => state.valueConverter)
  const dispatch = useDispatch();
  const history = useHistory();

  const fetchData = async (pNo = pageNo, pSize = pageSize, sQuery = searchQuery) => {
    try {
      setLoading(true);
      const body: any = {
        pageNo: Number(pNo) || 1,
        pageSize: Number(pSize) || 25,
        searchItem: sQuery || ''
      };
      const response = (await dispatch(getInvestorList(body))) as any;
      console.log("FULL RESPONSE", response);

      console.log(
        "INVESTOR COUNT",
        response?.length
      );

      console.log(
        "FIRST INVESTOR",
        response?.[0]
      );
      setInvestorList(response);

      const firstRow = response?.[0];
      const tp = Number(firstRow?.TotalPages) || 1;
      const tr = Number(firstRow?.TotalRows) || (response?.length || 0);
      setTotalPages(tp);
      setTotalRows(tr);

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log((e as Error).message);
    }
  };

  useEffect(() => {
    let isComponentActive = true;
    setInvestorList([]);
    (async function () {
      if (!isComponentActive) return;
      await fetchData(1, pageSize, '');
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  const tableData = investorList || [];
  const handleSearchClick = async () => {
    if (searchInput && searchInput.trim().length < 3) {
      alert('Please enter at least 3 characters to search.');
      return;
    }
    const q = searchInput.trim();
    setSearchQuery(q);
    setPageNo(1);
    await fetchData(1, pageSize, q);
  };

  const handlePageSizeChange = async (event: SelectChangeEvent) => {
    const newSize = Number(event.target.value) || 25;
    setPageSize(newSize);
    setPageNo(1);
    await fetchData(1, newSize, searchQuery);
  };

  const handlePrev = async () => {
    if (pageNo <= 1) return;
    const nextPage = pageNo - 1;
    setPageNo(nextPage);
    await fetchData(nextPage, pageSize, searchQuery);
  };

  const handleNext = async () => {
    if (pageNo >= totalPages) return;
    const nextPage = pageNo + 1;
    setPageNo(nextPage);
    await fetchData(nextPage, pageSize, searchQuery);
  };

  const getPageNumbers = (current: number, total: number) => {
    const maxVisible = 7;
    if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);

    let start = current - Math.floor(maxVisible / 2);
    let end = current + Math.floor(maxVisible / 2);

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <CommonLayout>
      <DashboardLayout>
        <>
          <Box
            sx={{
              bgcolor: 'common.white',
              boxShadow: '0px 4px 28px 2px rgba(0, 0, 0, 0.08)',
              borderRadius: '5px',
            }}>
            <Box
              sx={{
                display: { xs: 'block', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
              }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', mt: { xs: 2, sm: 0 }, pt: 3, pl: 3 }}>
                <FormControl sx={{ width: 250, mr: 2 }} variant="outlined">
                  <OutlinedInput
                    value={searchInput}
                    onChange={async ({ target: { value } }) => {
                      setSearchInput(value);
                      const q = value.trim();
                      if (q.length < 3) {
                        setSearchQuery('');
                        setPageNo(1);
                        try {
                          await fetchData(1, pageSize, '');
                        } catch (err) {
                          console.error((err as Error).message);
                        }
                        return;
                      }
                      if (q.length >= 3) {
                        setSearchQuery(q);
                        setPageNo(1);
                        try {
                          await fetchData(1, pageSize, q);
                        } catch (err) {
                          console.error((err as Error).message);
                        }
                      }
                    }}
                    sx={{ '& .MuiOutlinedInput-input': { py: 1, fontSize: 13 } }}
                    id="search-field"
                    placeholder="Search (min. 3 chars)"
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <IconButton
                  aria-label="search"
                  onClick={handleSearchClick}
                  disabled={isLoading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    mr: 2,
                    p: 1.1,
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: "space-between", flexDirection: "column", alignItems: 'center', mt: { xs: 2, sm: 0 }, pt: 3, pr: 3 }}>
                <AmountUnit />
              </Box>
            </Box>
            {isLoading ? (
              <LinearProgress sx={{ mt: 2.5 }} />
            ) : (
              <>
                <Box
                  mt={2.5}
                  sx={{ maxWidth: { lg: '100%' }, maxHeight: { md: '55vh', lg: '67vh' }, overflow: 'auto' }}
                >
                  {investorList && (
                    <DataTable
                      isLoading={isLoading}
                      tableHeader={[...headers()]}
                      tableData={tableData}
                      rowCustomStyles={{
                        '&:last-child td, &:last-child th': { border: 0, },
                        '.MuiTableCell-body ': { color: '#686e72' },
                        background: 'transparent',
                      }}
                      boxStyles={{
                        minWidth: 500,
                      }}
                    />
                  )}
                </Box>

                {investorList && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, px: 0, justifyContent: 'space-between', flexDirection: { xs: "column", sm: "row" } }}>
                    <FormControl sx={{ ml: 3, }}>
                      <Select
                        style={{ color: 'var(--title-color)', height: '1.7rem', width: '7rem' }}
                        value={String(pageSize)}
                        onChange={handlePageSizeChange}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Without label' }}
                        variant="standard"
                        sx={{
                          fontSize: 12,
                          lineHeight: '16px',
                          color: '#4E5056',
                          fontWeight: 500,
                          boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.15)',
                          borderRadius: '5px',
                          pl: 1,
                          background: ' #FFFFFF',
                        }}>
                        <MenuItem value="10" style={{ color: 'var(--title-color)' }}>10</MenuItem>
                        <MenuItem value="20" style={{ color: 'var(--title-color)' }}>20</MenuItem>
                        <MenuItem value="50" style={{ color: 'var(--title-color)' }}>50</MenuItem>
                        <MenuItem value="100" style={{ color: 'var(--title-color)' }}>100</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', }}>
                      <Pagination
                        count={totalPages}
                        page={pageNo}
                        color="primary"
                        onChange={async (_event, value) => {
                          if (value === pageNo) return;
                          setPageNo(value);
                          await fetchData(value, pageSize, searchQuery);
                        }}
                        sx={{
                          py: 1,
                          alignItems: "center",
                          "& .MuiPaginationItem-firstLast": {
                            display: { xs: "none", md: "inline-flex" },
                          },
                          "& .MuiPaginationItem-previousNext": {
                            display: { xs: "none", md: "inline-flex" },
                          },
                        }}

                      />
                    </Box>
                    <Box sx={{ minWidth: 200, display: 'flex', justifyContent: 'center', pl: { xs: 2.5, sm: 0 } }}>
                      <Typography variant="body2">
                        Page {pageNo} of {totalPages} ({totalRows} records)
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      </DashboardLayout>
    </CommonLayout>
  );
}
